/**
 * Decks API hooks - Global sync via Jotai + TanStack Query mutations
 *
 * Architecture:
 * - useDecksSync() subscribes to Firestore and populates decksStateAtom
 * - useDeckCardsSync() subscribes to flat deck_cards collection (single subscription)
 * - Returns isLoading=true until first snapshot, used by app initialization
 * - All consumers read from the shared Jotai atom via useDecksQuery()
 * - Active deck is derived from global atoms via activeDeckAtom
 */
import { useCallback, useEffect, useState } from 'react';

import { useMutation } from '@tanstack/react-query';
import type { DocumentData, Timestamp } from 'firebase/firestore';
import { collection, onSnapshot, orderBy, query } from 'firebase/firestore';
import { useAtomValue, useSetAtom } from 'jotai';

import { useFirestoreCollectionSync } from '@/hooks/useFirestoreSync';
import {
  addDeckCard,
  addDeckCards,
  addOrMergeDeckCards,
  bulkAddOrUpdateDeckCards,
  bulkUpdateDeckCardZones,
  createDeck,
  deleteDeck,
  duplicateDeck,
  removeDeckCard,
  removeDeckCards,
  updateDeck,
  updateDeckCard,
  type AddDeckCardInput,
  type CreateDeckInput,
  type UpdateDeckCardInput,
  type UpdateDeckInput,
} from '@/services/decks';
import { auth, firestore } from '@/services/firebase';
import type { Deck, DeckCard, DeckZone } from '@/types/deck';
import { allDeckCardsAtom } from '../state/allDeckCards';
import { activeDeckAtom, activeDeckIdAtom, decksStateAtom } from '../state/decksAtom';

// Re-export state atoms for consumers who need direct access
export { allDeckCardsAtom } from '../state/allDeckCards';
export { activeDeckAtom, activeDeckIdAtom, activeDeckStateAtom, decksStateAtom } from '../state/decksAtom';

// Re-export cross-deck usage hooks
export { useCrossDeckUsage, useDeckCountForCard, useTotalNeededAcrossDecks, type DeckUsage } from './useCrossDeckUsage';

// ============================================================================
// Helpers
// ============================================================================

/** Convert Firestore Timestamp to Date, with fallback */
function toDate(value: Timestamp | Date | undefined | null): Date {
  if (!value) return new Date();
  if (value instanceof Date) return value;
  // Firestore Timestamp has toDate() method
  if (typeof (value as Timestamp).toDate === 'function') {
    return (value as Timestamp).toDate();
  }
  return new Date();
}

/** Transform raw Firestore document to Deck */
function docToDeck(doc: DocumentData): Deck {
  return {
    id: doc.id,
    name: doc.name,
    description: doc.description ?? null,
    format: doc.format ?? null,
    status: doc.status,
    is_locked: doc.is_locked ?? false,
    cover_card_id: doc.cover_card_id ?? null,
    enforce_legality: doc.enforce_legality ?? true,
    card_count: doc.card_count ?? 0,
    ai_assessment: doc.ai_assessment ?? null,
    created_at: toDate(doc.created_at),
    updated_at: toDate(doc.updated_at),
  };
}

// ============================================================================
// Decks List Sync Hook
// ============================================================================

/**
 * Syncs decks list from Firestore to the decksStateAtom.
 * Returns true while loading (until first snapshot arrives).
 *
 * @param enabled - Whether sync is enabled (typically: isAuthenticated)
 */
export function useDecksSync(enabled: boolean): boolean {
  const transform = useCallback((docs: DocumentData[]): Deck[] => {
    // Convert documents and sort by updated_at descending
    return docs.map(docToDeck).sort((a, b) => {
      const aTime = a.updated_at?.getTime() ?? 0;
      const bTime = b.updated_at?.getTime() ?? 0;
      return bTime - aTime;
    });
  }, []);

  const queryFactory = useCallback(() => {
    const userId = auth.currentUser?.uid;
    if (!userId) return null;
    return query(collection(firestore, 'users', userId, 'decks'), orderBy('updated_at', 'desc'));
  }, []);

  return useFirestoreCollectionSync({
    atom: decksStateAtom,
    transform,
    queryFactory,
    enabled,
  });
}

// ============================================================================
// All Deck Cards Sync Hook
// ============================================================================

/**
 * Syncs ALL deck cards to allDeckCardsAtom using flat collection pattern.
 * Single subscription to users/{uid}/deck_cards collection.
 *
 * @param enabled - Whether sync is enabled (typically: isAuthenticated)
 * @returns true while loading (until first snapshot arrives)
 */
export function useDeckCardsSync(enabled: boolean): boolean {
  const setAllDeckCards = useSetAtom(allDeckCardsAtom);
  const [isLoading, setIsLoading] = useState(enabled);

  useEffect(() => {
    const userId = auth.currentUser?.uid;

    // Clear state if not enabled or no user
    if (!enabled || !userId) {
      setAllDeckCards({ data: undefined, isLoading: false, error: null });
      setIsLoading(false);
      return;
    }

    // Single subscription to all deck cards
    const cardsRef = collection(firestore, 'users', userId, 'deck_cards');

    const unsub = onSnapshot(
      cardsRef,
      (snapshot) => {
        const cards: DeckCard[] = snapshot.docs.map((docSnap) => {
          const data = docSnap.data();
          return {
            id: docSnap.id,
            deck_id: data.deck_id,
            scryfall_id: data.scryfall_id,
            card_name: data.card_name ?? null,
            quantity: data.quantity,
            zone: data.zone,
            added_at: data.added_at?.toDate() ?? new Date(),
          };
        });

        setAllDeckCards({
          data: cards,
          isLoading: false,
          error: null,
        });
        setIsLoading(false);
      },
      (error) => {
        console.error('Error syncing deck cards:', error);
        setAllDeckCards((prev) => ({
          ...prev,
          isLoading: false,
          error: error as Error,
        }));
        setIsLoading(false);
      },
    );

    return () => unsub();
  }, [enabled, setAllDeckCards]);

  return isLoading;
}

// ============================================================================
// Active Deck Hook
// ============================================================================

/**
 * Sets the active deck ID for the deck editor.
 * The active deck data is derived from global atoms (no additional subscriptions).
 *
 * @param deckId - The ID of the deck to edit (null to clear)
 */
export function useSetActiveDeckId(deckId: string | null): void {
  const setActiveDeckId = useSetAtom(activeDeckIdAtom);

  useEffect(() => {
    setActiveDeckId(deckId);

    // Clear on unmount
    return () => {
      setActiveDeckId(null);
    };
  }, [deckId, setActiveDeckId]);
}

/**
 * @deprecated Use useSetActiveDeckId instead. This is kept for backward compatibility.
 */
export const useActiveDeckSync = useSetActiveDeckId;

// ============================================================================
// Public Query Hooks
// ============================================================================

/**
 * Read-only hook for user's decks list.
 * Data is populated by useDecksSync and kept in sync via subscription.
 */
export function useDecksQuery() {
  return useAtomValue(decksStateAtom);
}

/**
 * Read-only hook for ALL deck cards across ALL decks.
 * Data is populated by useDeckCardsSync and kept in sync via subscription.
 */
export function useAllDeckCardsQuery() {
  return useAtomValue(allDeckCardsAtom);
}

/**
 * Get a single deck from the decks list by ID.
 */
export function useDeckQuery(deckId: string | null) {
  const { data, isLoading, error } = useAtomValue(decksStateAtom);

  if (!deckId) {
    return { data: null, isLoading: false, error: null };
  }

  return {
    data: data?.find((d) => d.id === deckId) ?? null,
    isLoading,
    error,
  };
}

/**
 * Read the active deck state (for deck editor).
 * Derived from global decksStateAtom and allDeckCardsAtom.
 * Set the active deck ID with useSetActiveDeckId().
 */
export function useActiveDeckQuery() {
  return useAtomValue(activeDeckAtom);
}

// ============================================================================
// Deck Mutations
// ============================================================================

/**
 * Mutation hook for creating a deck
 */
export function useCreateDeckMutation() {
  return useMutation({
    mutationFn: (data: CreateDeckInput) => createDeck(data),
  });
}

/**
 * Mutation hook for updating a deck
 */
export function useUpdateDeckMutation() {
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateDeckInput }) => updateDeck(id, data),
  });
}

/**
 * Mutation hook for deleting a deck
 */
export function useDeleteDeckMutation() {
  return useMutation({
    mutationFn: (deckId: string) => deleteDeck(deckId),
  });
}

/**
 * Mutation hook for duplicating a deck
 */
export function useDuplicateDeckMutation() {
  return useMutation({
    mutationFn: ({ deckId, newName }: { deckId: string; newName?: string }) => duplicateDeck(deckId, newName),
  });
}

// ============================================================================
// Deck Card Mutations
// ============================================================================

/**
 * Mutation hook for adding a single card to a deck
 */
export function useAddDeckCardMutation() {
  return useMutation({
    mutationFn: ({ deckId, card }: { deckId: string; card: AddDeckCardInput }) => addDeckCard(deckId, card),
  });
}

/**
 * Mutation hook for adding multiple cards to a deck
 */
export function useAddDeckCardsMutation() {
  return useMutation({
    mutationFn: ({ deckId, cards }: { deckId: string; cards: AddDeckCardInput[] }) => addDeckCards(deckId, cards),
  });
}

/**
 * Mutation hook for adding multiple cards to a deck, merging into existing slots
 * (one slot per card edition per zone).
 */
export function useAddOrMergeDeckCardsMutation() {
  return useMutation({
    mutationFn: ({ deckId, cards }: { deckId: string; cards: AddDeckCardInput[] }) => addOrMergeDeckCards(deckId, cards),
  });
}

/**
 * Mutation hook for updating a card in a deck (quantity/zone)
 */
export function useUpdateDeckCardMutation() {
  return useMutation({
    mutationFn: ({ deckId, cardId, data }: { deckId: string; cardId: string; data: UpdateDeckCardInput }) =>
      updateDeckCard(deckId, cardId, data),
  });
}

/**
 * Mutation hook for removing a single card from a deck
 */
export function useRemoveDeckCardMutation() {
  return useMutation({
    mutationFn: ({ deckId, cardId }: { deckId: string; cardId: string }) => removeDeckCard(deckId, cardId),
  });
}

/**
 * Mutation hook for removing multiple cards from a deck
 */
export function useRemoveDeckCardsMutation() {
  return useMutation({
    mutationFn: ({ deckId, cardIds }: { deckId: string; cardIds: string[] }) => removeDeckCards(deckId, cardIds),
  });
}

/**
 * Mutation hook for bulk moving multiple deck cards to new zones
 */
export function useBulkUpdateDeckCardZonesMutation() {
  return useMutation({
    mutationFn: ({ deckId, updates }: { deckId: string; updates: { cardId: string; zone: DeckZone }[] }) =>
      bulkUpdateDeckCardZones(deckId, updates),
  });
}

/**
 * Mutation hook for bulk adding new cards and updating existing card quantities in a deck
 */
export function useBulkAddOrUpdateDeckCardsMutation() {
  return useMutation({
    mutationFn: ({
      deckId,
      toAdd,
      toUpdate,
    }: {
      deckId: string;
      toAdd: AddDeckCardInput[];
      toUpdate: { cardId: string; newQuantity: number }[];
    }) => bulkAddOrUpdateDeckCards(deckId, toAdd, toUpdate),
  });
}
